import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { QRCodeSVG } from "qrcode.react";
import ShareQRDialog from "@/components/ShareQRDialog";
import { toast } from "sonner";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Send,
  CreditCard,
  TrendingUp,
  Sparkles,
  Eye,
  EyeOff,
  Copy,
} from "lucide-react";

const ZEN = "https://static.prod-images.emergentagent.com/jobs/06b5cb7e-c888-4c81-b88c-5244c1d0af32/images/cc59150a93d9427a62637e0fbdd0dfcfece6cc59cabc4e8149e67d6bd7803663.png";

function fmt(n) { return `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

export default function Dashboard() {
  const { user, balance, refreshBalance } = useAuth();
  const [txs, setTxs] = useState([]);
  const [show, setShow] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    refreshBalance();
    api.get("/wallet/transactions?limit=6").then(({ data }) => setTxs(data.transactions || []));
  }, [refreshBalance]);

  const mode = user?.banking_mode || "demo";
  const primaryBal = balance ? (mode === "demo" ? balance.balance_demo : balance.balance_live) : 0;
  const secondaryBal = balance ? (mode === "demo" ? balance.balance_live : balance.balance_demo) : 0;
  const secondaryLabel = mode === "demo" ? "Live" : "Demo";

  const summary = (() => {
    if (!txs.length) return "You're just getting started. Try a deposit to see your balance grow.";
    const week = txs.filter((t) => {
      const dt = new Date(t.created_at);
      return Date.now() - dt.getTime() < 7 * 24 * 3600 * 1000;
    });
    const inflow = week.filter((t) => t.type === "deposit" || t.type === "transfer_in").reduce((s, t) => s + (t.amount || 0), 0);
    const outflow = week.filter((t) => t.type === "withdraw" || t.type === "transfer_out").reduce((s, t) => s + (t.amount || 0), 0);
    if (inflow === 0 && outflow === 0) return "A quiet week — no money moved.";
    if (inflow > outflow) return `You added ${fmt(inflow - outflow)} this week. A grounded rise.`;
    return `You spent ${fmt(outflow - inflow)} more than you received this week.`;
  })();

  return (
    <AppLayout>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-eyebrow text-[#4F4D45]">Good to see you</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1" data-testid="dashboard-greeting">
            {user?.name?.split(" ")[0]}.
          </h1>
        </div>
        <div className="flex items-center gap-2 text-xs text-[#4F4D45]">
          <Sparkles className="w-3.5 h-3.5 text-[#A85943]" />
          <span data-testid="dashboard-insight">{summary}</span>
        </div>
      </div>

      {user?.kyc_status && user.kyc_status !== "approved" && (
        <div className="mb-8 fv-card p-4 md:p-5 flex items-start gap-3 border-[#B58957]/60 bg-[#B58957]/[0.08]" data-testid="dashboard-kyc-banner">
          <div className="w-8 h-8 rounded-lg bg-[#B58957]/20 text-[#B58957] flex items-center justify-center shrink-0">!</div>
          <div className="text-sm flex-1">
            <div className="font-medium text-[#F0ECE0]">
              {user.kyc_status === "submitted" ? "Identity review pending" : "Identity not verified"}
            </div>
            <div className="text-[#9DA39A] text-xs mt-0.5 leading-relaxed">
              {user.kyc_status === "submitted"
                ? "Deposits, withdrawals and transfers unlock as soon as KYC is approved — usually under 24 hours."
                : "Your KYC application needs attention. Reach out to support@finvault.capital to continue."}
            </div>
          </div>
        </div>
      )}

      {/* Bento */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
        <div className="md:col-span-4 fv-card p-8 bg-[#1B3526] text-white relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-[#264C39]/40 blur-3xl" />
          <div className="relative">
            <div className="flex items-center justify-between">
              <div className="text-eyebrow text-white/70">{mode === "demo" ? "Demo balance" : "Live balance"}</div>
              <button onClick={() => setShow(!show)} className="text-white/70 hover:text-white" data-testid="dashboard-toggle-balance">
                {show ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
            </div>
            <div className="font-display text-5xl md:text-6xl font-bold tracking-tight mt-3" data-testid="dashboard-primary-balance">
              {show ? fmt(primaryBal) : "•••••"}
            </div>
            <div className="mt-3 text-white/70 text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Wallet ID: <span className="font-mono text-white" data-testid="dashboard-wallet-id">{user?.wallet_id}</span>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/app/deposit" data-testid="dashboard-action-deposit">
                <Button className="bg-white text-[#1B3526] hover:bg-white/90 rounded-lg">
                  <ArrowDownToLine className="w-4 h-4 mr-2" /> Deposit
                </Button>
              </Link>
              <Link to="/app/withdraw" data-testid="dashboard-action-withdraw">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white rounded-lg bg-transparent">
                  <ArrowUpFromLine className="w-4 h-4 mr-2" /> Withdraw
                </Button>
              </Link>
              <Link to="/app/transfer" data-testid="dashboard-action-transfer">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white rounded-lg bg-transparent">
                  <Send className="w-4 h-4 mr-2" /> Transfer
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <div className="md:col-span-2 fv-card p-6 flex flex-col" data-testid="dashboard-account-card">
          <div className="text-eyebrow text-[#4F4D45]">Your account number</div>
          <div className="mt-2 flex items-center justify-between gap-3">
            <div className="font-mono text-xl tracking-[0.18em] font-semibold" data-testid="dashboard-account-number">
              {balance?.account_number || user?.account_number || "—"}
            </div>
            <button
              onClick={async () => {
                const num = balance?.account_number || user?.account_number;
                if (!num) return;
                try {
                  await navigator.clipboard.writeText(num);
                  toast.success("Account number copied.");
                } catch {
                  toast.error("Couldn't copy. Long-press to select.");
                }
              }}
              className="text-[#4F4D45] hover:text-[#1B3526] p-1.5 rounded-md hover:bg-white/10 transition-colors"
              aria-label="Copy account number"
              data-testid="dashboard-account-copy"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <div className="text-xs text-[#4F4D45] mt-1">Share this to receive transfers.</div>

          {(balance?.account_number || user?.account_number) && (
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="mt-5 flex items-center gap-4 w-full text-left rounded-xl hover:bg-white/5 transition-colors p-1 -m-1"
              data-testid="dashboard-account-share"
              aria-label="Share my QR"
            >
              <div className="bg-white rounded-xl p-2 border border-[#D2CDBE] shrink-0" data-testid="dashboard-account-qr">
                <QRCodeSVG
                  value={`finvault://transfer?account=${balance?.account_number || user?.account_number}`}
                  size={96}
                  bgColor="#FFFFFF"
                  fgColor="#0E1A14"
                  level="M"
                  includeMargin={false}
                />
              </div>
              <div>
                <div className="text-sm font-medium text-[#F0ECE0]">Share my QR →</div>
                <div className="text-xs text-[#4F4D45] leading-relaxed mt-0.5">
                  Download as image or send via WhatsApp / iMessage.
                </div>
              </div>
            </button>
          )}

          <div className="mt-6 pt-5 border-t border-white/10">
            <div className="text-eyebrow text-[#4F4D45]">{secondaryLabel} balance</div>
            <div className="font-display text-2xl font-bold tracking-tight mt-1" data-testid="dashboard-secondary-balance">{show ? fmt(secondaryBal) : "•••••"}</div>
          </div>
          <div className="mt-auto pt-5">
            <Link to="/app/cards" className="text-sm text-[#1B3526] hover:underline inline-flex items-center gap-2" data-testid="dashboard-link-cards">
              <CreditCard className="w-4 h-4" /> Manage cards
            </Link>
          </div>
        </div>

        <div className="md:col-span-4 fv-card p-0 overflow-hidden">
          <div className="p-6 flex items-end justify-between">
            <div>
              <div className="text-eyebrow text-[#4F4D45]">Recent activity</div>
              <div className="font-display text-2xl font-medium mt-1">Last six moves</div>
            </div>
            <Link to="/app/transactions" className="text-sm text-[#1B3526] hover:underline" data-testid="dashboard-link-transactions">View all →</Link>
          </div>
          <div className="border-t border-[#D2CDBE]">
            {txs.length === 0 ? (
              <div className="p-10 text-center">
                <img src={ZEN} className="w-24 h-24 mx-auto rounded-full object-cover" alt="No activity" />
                <p className="text-[#4F4D45] mt-4">No transactions yet. Make your first deposit to bring this to life.</p>
              </div>
            ) : (
              <ul className="divide-y divide-[#D2CDBE]" data-testid="dashboard-tx-list">
                {txs.map((t) => <TxRow key={t.id} t={t} />)}
              </ul>
            )}
          </div>
        </div>

        <div className="md:col-span-2 fv-card p-6">
          <div className="text-eyebrow text-[#4F4D45]">Security</div>
          <div className="font-display text-2xl font-medium mt-1">{user?.two_factor_enabled ? "2FA active" : "Strengthen 2FA"}</div>
          <p className="text-sm text-[#4F4D45] mt-2 leading-relaxed">
            {user?.two_factor_enabled
              ? "Your account is protected with TOTP. Login codes come from your authenticator."
              : "Add an authenticator app for stronger sign-in. We'll show you how in under a minute."}
          </p>
          <Link to="/app/settings" data-testid="dashboard-security-link">
            <Button className="mt-5 bg-[#1B3526] hover:bg-[#0F2517] w-full">Open security</Button>
          </Link>
        </div>
      </div>

      <ShareQRDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        accountNumber={balance?.account_number || user?.account_number}
        name={user?.name}
      />
    </AppLayout>
  );
}

function TxRow({ t }) {
  const sign = (t.type === "deposit" || t.type === "transfer_in") ? "+" : "−";
  const color = sign === "+" ? "text-[#264C39]" : "text-[#992F2F]";
  return (
    <li className="px-6 py-4 flex items-center justify-between">
      <div>
        <div className="font-medium text-sm">{t.description || t.type}</div>
        <div className="text-xs text-[#4F4D45]">
          {new Date(t.created_at).toLocaleString()} · {t.mode} · {t.status}
        </div>
      </div>
      <div className={`font-mono ${color}`}>{sign}{fmt(t.amount)}</div>
    </li>
  );
}
